<template>
  <div :class="{'md:relative': !target}">
    <portal to="context-menu-destination" v-if="showMobileComp">
      <transition name="fade" appear>
        <div
          v-if="visible"
          class="context-menu-backdrop"
          :class="classList"
          @click="hide"
        />
      </transition>

      <transition name="slide" appear>
        <div
          :class="classList"
          class="fixed bottom-0 z-20 w-full transition-all"
          @click="hide"
          v-if="visible"
        >
          <div class="context-menu-wrapper">
            <slot />
          </div>

          <span
            class="context-menu-close"
            @click="hide"
          >
            <aha-icon
              name="close"
              width="16"
              height="16"
              viewBox="0 0 24 24"
            />
          </span>
        </div>
      </transition>
    </portal>

    <transition name="fade" appear>
      <div
        ref="contextMenu"
        class="absolute"
        style="z-index: 9999"
        :class="{
          'w-full': fullWidth,
        }"
        v-click-outside="hide"
        @click="hide"
        :style="style"
        v-if="visible && !showMobileComp"
      >
        <div class="context-menu-wrapper">
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  SVGPathElement, SVGElement, SVGTextElement, SVGSVGElement,
} from '@svgdotjs/svg.js'

import AhaIcon from '../Icon.vue'

const breakPointScreen = 768 // md

export default {
  components: {
    AhaIcon,
  },
  props: {
    classList: {
      type: String,
      default: '',
    },
    target: {
      type: String,
      default: null,
    },
    targetClick: {
      type: [HTMLSpanElement, HTMLImageElement, HTMLDivElement, HTMLCanvasElement, SVGPathElement, HTMLElement, HTMLParagraphElement, SVGElement, SVGTextElement, SVGSVGElement],
      default: null,
    },
    variant: {
      // options: (target side - context side) leftright, rightleft, leftleft, rightright
      type: String,
      default: 'leftleft',
    },
    x: {
      type: Number,
      default: null,
    },
    y: {
      type: Number,
      default: null,
    },
    offsetX: {
      type: Number,
      default: null,
    },
    offsetY: {
      type: Number,
      default: null,
    },
    layerX: {
      type: Number,
      default: null,
    },
    layerY: {
      type: Number,
      default: null,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    forceMenuPosition: {
      type: Boolean,
      default: false,
    },
    isMenuInCanvas: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      style: {},
      visible: false,
    }
  },
  computed: {
    showMobileComp() {
      return this.visible && window.innerWidth < 768
    },
  },
  watch: {
    target() {
      this.$nextTick(() => {
        this.moveToPosition()
      })
    },
  },
  methods: {
    show() {
      this.visible = true
      this.$nextTick(() => {
        this.moveToPosition()
      })
    },
    hide() {
      this.visible = false
      this.$emit('hide')
    },
    moveToPosition() {
      if (window.innerWidth < breakPointScreen) {
        this.moveOnMobile()
      } else {
        this.moveOnDesktop()
      }
    },
    moveOnMobile() {
    },
    moveOnDesktop() {
      const menu = this.$refs.contextMenu
      const el = document.getElementById(this.target)
      if (!el || !menu) {
        return
      }
      let { top, right } = el.getBoundingClientRect()
      const { left, width } = el.getBoundingClientRect()
      if (this.isMenuInCanvas) {
        this._setMoveOnDesktopCanvas(top, left, width, menu, el)
        return
      }

      // When x, y is specified, use x, y instead
      if (this.x !== null) {
        right = this.x
      }

      if (this.y !== null) {
        top = this.y
      }

      let style = { }
      let menuTop = top + window.scrollY

      if (!this.forceMenuPosition) {
        if (top + menu.offsetHeight > window.innerHeight) {
          menuTop -= menu.offsetHeight
          style = { ...style, top: `${menuTop}px` }
        } else {
          style = { ...style, top: `${menuTop}px` }
        }

        if (right + menu.offsetWidth > window.innerWidth) {
          right = left - menu.offsetWidth
        }
        this.style = { ...style, left: `${right}px` }
      } else {
        // This make it posible to set the context menu position
        // based on the nearest 'relative' element
        this.style = {
          top: `${top}px`,
          right: `${right}px`,
        }
      }
    },

    _setMoveOnDesktopCanvas(top, left, width, menu, el) {
      const hasFlexClass = this.targetClick.classList && this.targetClick.classList.contains('flex')
      const canvasHeight = (el && el.style.height) ? Number(el.style.height.replace(/\D/g, '')) : window.innerHeight
      // const canvasWidth = (el && el.style.width) ? Number(el.style.width.replace(/\D/g, '')) : window.innerWidth
      // const isCanvasBlockTarget = this.targetClick.closest('.element-editor') || this.targetClick.closest('.mce-editor')
      // When x, y is specified, use x, y instead
      if (this.x !== null) {
        // Display flex on canvas affects layer
        if (this.offsetX < this.layerX) {
          if (this.targetClick.id === 'canvas-question-image') {
            left += this.offsetX
          } else {
            left = this.x - left
          }
        }
        if (this.offsetX >= this.layerX) {
          left = this.x - left + this.offsetX - this.layerX
        }
      }

      if (this.y !== null) {
        if (this.offsetY < this.layerY && this.targetClick.id === 'canvas-question-image') {
          top += this.offsetY
        }
        if (hasFlexClass) {
          top = this.y - top
        }
        if (this.offsetY >= this.layerY && !hasFlexClass) {
          top = this.y - top + this.offsetY - this.layerY
        }
      }
      let menuTop = top + window.scrollY
      // console.log('top', top, 'menu height', menu.offsetHeight, top + menu.offsetHeight > canvasHeight)
      if (top + menu.offsetHeight > canvasHeight) {
        menuTop = canvasHeight - menu.offsetHeight
        menuTop = menuTop < 0 ? 0 : menuTop
      }

      if (left > width - 100) {
        left -= menu.offsetWidth
      }
      // console.log('final top', menuTop)
      this.style = { left: `${left}px`, top: `${menuTop}px` }
    },
  },
}
</script>

<style lang="scss">
.context-menu-wrapper {
  @apply relative z-40 flex flex-col md:border-0.5 px-4 md:px-1 pt-2 pb-4 md:pt-1 md:pb-1 md:mt-3;
  @apply rounded-b-none rounded-t-md md:rounded-b-md md:w-auto w-full;
  @apply md:shadow-dropdown bg-base-0 md:border-base-30 md:min-w-36;

  .divider {
    &:nth-child(n+3) {
      @apply border-0.5 border-base-30;
    }
  }
}

.context-menu-close {
  @apply text-base-0 absolute cursor-pointer right-3 -top-8 z-40;
}

.context-menu-backdrop {
  @apply fixed inset-0 bg-base-100 opacity-50 z-10;
}

.fade-enter-active,
.fade-leave-active {
  @apply transition-opacity;
}

.fade-enter,
.fade-leave-to {
  @apply opacity-0;
}

.slide-enter-active,
.slide-leave-enter {
  transform: translateY(0);
}

.slide-enter,
.slide-leave-to {
  transform: translateY(100%);
}

.force-z-index{
  &.context-menu-backdrop{
    z-index: 99999;
  }
  &.transition-all{
    z-index: 99999;
  }
}
</style>
